// @react
import React, { useState, useContext } from 'react'
// @utils
import Helper from 'utils/Helper.class'
import useBookmarkToggleAction from 'modules/common/hooks/useBookmark'
// @types
import Card, {
  PropsType as CardProps,
  Tag,
} from '__COMPONENTS_TO_MIGRATE/CardNew'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
// @common
import { SnackbarContext } from 'modules/common/context/snackbar'
import colors from '@bit/atd.web.colors/dist/colors'
// @graphql
import {
  ArticleSearchItem,
  BlogSearchItem,
  ConferenceSearchItem,
  CoursePurchasableSearchItem,
  CourseSearchItem,
  FilterOrder,
  GetUserContentQuery,
  GetUserContentQueryVariables,
  IAbstractSearchItem,
  IContentSearchItem,
  MeetingSessionSearchItem,
  PodcastSearchItem,
  PublicationSearchItem,
  SponsorItem,
  UserContentOrderByList,
  UserContentRelationship,
  UserSearchItem,
  VideoSearchItem,
  WebinarSearchItem,
} from 'lib/graphql/graphqlTypes'
import { useQuery } from '@apollo/client'
import client from 'graphql/Apollo/apolloClient'
import { GET_ALL_BOOKMARKS } from 'graphql/queries/userContent.query'
// @components
import CardMicro from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Micro'
import Modal from '@bit/atd.web.modal'
import Button from '@bit/atd.web.button'
import Divider from '@bit/atd.web.divider'
import Grid from '@bit/atd.web.grid'
import Spacer from '@bit/atd.web.spacer'
import { GraphQLError } from 'graphql'
import useContentCardFormatters from './hooks/useContentCardFormatters'

/********************************************
 *  TYPES
 *********************************************/
export type ContentTypes =
  | 'IAbstractSearchItem'
  | 'ArticleSearchItem'
  | 'BlogSearchItem'
  | 'ConferenceSearchItem'
  | 'CourseSearchItem'
  | 'PublicationSearchItem'
  | 'PageSearchItem'
  | 'MeetingSessionSearchItem'
  | 'UserSearchItem'
  | 'VideoSearchItem'
  | 'WebinarSearchItem'
  | 'PodcastSearchItem'
  | undefined

type BookmarkableSearchItem =
  | ArticleSearchItem
  | BlogSearchItem
  | CourseSearchItem
  | ConferenceSearchItem
  | VideoSearchItem
  | PublicationSearchItem

type SelfProps = {
  typename: ContentTypes
  item: IAbstractSearchItem
  variant?: string
  type?: CardProps['type']
  layout?: CardProps['layout']
  autoLayoutWidth?: CardProps['autoLayoutWidth']
  sponsors?: SponsorItem[]
}
type PropsType = SelfProps

/**
 *
 * @param props
 * @constructor
 */
const ContentCard = (props: PropsType) => {
  /********************************************
   *  PROPS
   *********************************************/
  const {
    item: contentItem,
    typename,
    variant,
    type,
    autoLayoutWidth,
    layout,
  } = props

  /********************************************
   *  STATE
   *********************************************/
  const [isLoading, setIsMutationLoading] = useState<boolean>(false)
  const [itemListModal, setItemListModal] = useState<string | null>(null)

  /********************************************
   *  HOOKS
   *********************************************/
  const snackbar = useContext(SnackbarContext)

  const {
    authorFormatter,
    detailFormatter,
    contentTypeFormatter,
  } = useContentCardFormatters({ setItemListModal })

  const { toggleBookmarkAction } = useBookmarkToggleAction()

  /********************************************
   *  GQL HOOKS
   *********************************************/
  const vars = {
    input: {
      page: 1,
      pageSize: 100,
      filters: {
        userRelationship: UserContentRelationship.BOOKMARKED,
      },
      orderBy: {
        order: FilterOrder.DESC,
        value: UserContentOrderByList.PUBLISHED_DATE,
      },
    },
  }

  const { data: contentData, refetch: contentRefetch } = useQuery<
    GetUserContentQuery,
    GetUserContentQueryVariables
  >(GET_ALL_BOOKMARKS, {
    fetchPolicy: 'network-only',
    variables: vars,
  })

  /************************************
   * CONSTANTS
   *************************************/

  const tag: Tag = contentItem.memberBenefit
    ? {
        text: 'Member Benefit',
        variant: 'subtle',
        color: 'default',
      }
    : undefined

  /************************************
   * FUNCTIONS: BOOKMARK
   *************************************/

  /**
   *
   * @param content
   * @param typename
   */
  const isBookmarkable = (
    content: IContentSearchItem,
    typename: string,
  ): boolean => {
    if (content.isBookmarkable !== undefined) {
      return content.isBookmarkable
    }

    switch (typename) {
      case 'ArticleSearchItem':
      case 'BlogSearchItem':
      case 'CourseSearchItem':
      case 'ConferenceSearchItem':
      case 'VideoSearchItem':
      case 'PublicationSearchItem':
        return true
    }
    return false
  }

  /**
   *
   */
  const getBookmark = (
    content: BookmarkableSearchItem,
    typename: string,
  ): {
    isBookmarkable: boolean
    callback: (content: BookmarkableSearchItem) => Promise<void>
    value: boolean
  } => {
    const value = !!(
      !!content?.isBookmarked ||
      contentData?.userContent.data.find(
        (subItem) => contentItem.id === subItem.id,
      )
    )

    return {
      isBookmarkable: isBookmarkable(content, typename),
      callback: () => bookmarkCallback(content, value),
      value,
    }
  }
  /**
   *
   * @param mutationErrors
   */
  const bookmarkCallbackHandler = async (
    mutationErrors: readonly GraphQLError[] | undefined,
  ): Promise<boolean> => {
    const content = contentItem as BookmarkableSearchItem

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: content.isBookmarked
          ? 'Bookmark successfully deleted!'
          : 'Bookmark successfully added!',
        show: true,
      })

      if (contentItem.hasOwnProperty('isBookmarked')) {
        //  setIsDeleted(true)
        client.cache.modify({
          id: client.cache.identify(contentItem),
          fields: {
            isBookmarked() {
              return !content.isBookmarked
            },
          },
        })
      }

      if (contentData?.userContent) {
        await contentRefetch()
      }
      return true
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: 'bookmark update error',
        show: true,
      })
    }
    return false
  }

  // const bookmark = getBookmark(item, typename)

  /**
   * bookmarkCallback
   */
  const bookmarkCallback = async (
    content: BookmarkableSearchItem,
    isBookmarked: boolean,
  ): Promise<void> => {
    setIsMutationLoading(true)
    const res = await toggleBookmarkAction(content.id, isBookmarked)
    await bookmarkCallbackHandler(res)
    setIsMutationLoading(false)
  }

  /************************************
   * FUNCTIONS: LIST MODAL
   *************************************/
  /**
   *
   * @param content
   */
  const listModal = (content: CourseSearchItem | ConferenceSearchItem) => {
    return (
      <Modal
        onClose={() => setItemListModal(null)}
        footerNoSeparator
        headerNoSeparator
        open={itemListModal === content.id}
        size="large"
        title="Dates and Locations"
        actions={
          <div>
            <Button variant="primary" size="medium" href={content.url}>
              More info
            </Button>
          </div>
        }
      >
        {listModalContent(content)}
      </Modal>
    )
  }

  /**
   *
   * @param content
   */
  const listModalContent = (
    content: CourseSearchItem | ConferenceSearchItem,
  ) => {
    return (
      <Grid container spacing={2}>
        {content.purchasableItems?.map(
          (item: CoursePurchasableSearchItem, index) => {
            return (
              <Grid item key={index} xs={12}>
                <Grid container>
                  <Grid item xs={6}>
                    {item?.location}
                  </Grid>
                  <Grid item xs={6}>
                    {Helper.globalDateFormatter(item?.beginDate)}
                  </Grid>
                </Grid>
                <Spacer size={1} />
                <Divider width="100%" color={colors.gray300} height="1px" />
              </Grid>
            )
          },
        )}
      </Grid>
    )
  }

  /********************************************
   *  RENDER
   *********************************************/
  const cardChooser = () => {
    switch (typename) {
      case 'ArticleSearchItem':
      case 'PageSearchItem':
      case 'BlogSearchItem': {
        const content = contentItem as IContentSearchItem
        const byLine = authorFormatter(content)

        const thumbnailType =
          typename === 'ArticleSearchItem'
            ? 'video'
            : content.contentTypes?.lvl1 ===
              'Performance Resource > TD at Work Guide'
            ? 'publication'
            : 'square'

        return (
          <Card
            layout={layout}
            autoLayoutWidth={autoLayoutWidth}
            type={type}
            isLoading={isLoading}
            thumbnail={{
              src: content?.imageUrl || '',
              type: thumbnailType,
              fullWidth: false,
            }}
            content={{
              title: content?.title,
              titleUrl: content?.url,
              titleTruncate: true,
              subTitle: content?.description || '',
              subTitleTruncate: type !== 'search',
              byLine,
            }}
            details={{}}
            footer={{
              contentType: contentTypeFormatter(content),
              tag,
              bookmark: getBookmark(
                content as BookmarkableSearchItem,
                typename,
              ),
            }}
          />
        )
      }
      case 'PublicationSearchItem': {
        const content = contentItem as PublicationSearchItem
        const byLine = authorFormatter(content)

        return (
          <Card
            layout={layout}
            isLoading={isLoading}
            autoLayoutWidth={autoLayoutWidth}
            type={type}
            thumbnail={{
              src: content?.imageUrl || '',
              type: 'publication',
              fullWidth: false,
            }}
            content={{
              title: content?.title,
              titleUrl: content?.url,
              titleTruncate: true,
              subTitle: content?.description || '',
              subTitleTruncate: type !== 'search',
              byLine,
            }}
            details={{}}
            footer={{
              contentType: contentTypeFormatter(content),
              tag,
              bookmark: getBookmark(content, typename),
            }}
          />
        )
      }
      case 'CourseSearchItem': {
        const content = contentItem as CourseSearchItem
        const details = detailFormatter(content)

        switch (variant) {
          case 'micro':
            return (
              <CardMicro
                thumbnail={{
                  src: content?.imageUrl || '',
                  label: 'In Progress',
                  clickable: true,
                }}
                title={content?.title}
                url={content.url}
              />
            )
          default:
            return (
              <>
                {listModal(content)}
                <Card
                  layout={layout}
                  isLoading={isLoading}
                  autoLayoutWidth={autoLayoutWidth}
                  type={type}
                  thumbnail={{
                    src: content?.imageUrl || '',
                    type: 'square',
                    fullWidth: false,
                  }}
                  content={{
                    title: content?.title,
                    titleUrl: content?.url,
                    titleTruncate: true,
                    subTitle: content?.description || '',
                    subTitleTruncate: type !== 'search',
                  }}
                  details={details}
                  footer={{
                    contentType: contentTypeFormatter(content),
                    tag,
                    bookmark: getBookmark(content, typename),
                  }}
                />
              </>
            )
        }
      }
      case 'ConferenceSearchItem': {
        const content = contentItem as ConferenceSearchItem
        const details = detailFormatter(content)

        return (
          <>
            {listModal(content)}
            <Card
              layout={layout}
              autoLayoutWidth={autoLayoutWidth}
              isLoading={isLoading}
              type={type}
              thumbnail={{
                src: content?.imageUrl || '',
                type: 'square',
                fullWidth: false,
              }}
              content={{
                title: content?.title,
                titleUrl: content?.url,
                titleTruncate: true,
                subTitle: content?.description || '',
                subTitleTruncate: type !== 'search',
              }}
              details={details}
              footer={{
                contentType: contentTypeFormatter(content),
                tag,
                bookmark: getBookmark(content, typename),
              }}
            />
          </>
        )
      }
      case 'VideoSearchItem':
      case 'PodcastSearchItem':
      case 'WebinarSearchItem': {
        const content = contentItem as
          | VideoSearchItem
          | PodcastSearchItem
          | WebinarSearchItem
        const byLine = authorFormatter(content)
        const timeStampMultiplier =
          typename === 'WebinarSearchItem' ? 1000 * 60 : 1
        return (
          <Card
            layout={layout}
            autoLayoutWidth={autoLayoutWidth}
            type={type}
            isLoading={isLoading}
            thumbnail={{
              src: content?.imageUrl || '',
              type: 'video',
              fullWidth: false,
              timestamp:
                (content.duration &&
                  Helper.timestampFormatter(
                    content.duration,
                    timeStampMultiplier,
                  )) ||
                '',
            }}
            content={{
              title: content?.title,
              titleUrl: content?.url,
              titleTruncate: true,
              subTitle: content?.description || '',
              subTitleTruncate: type !== 'search',
              byLine,
              sponsors: (contentItem as WebinarSearchItem)?.sponsors || [],
            }}
            details={{}}
            footer={{
              contentType: contentTypeFormatter(content),
              tag,
              bookmark: getBookmark(
                content as BookmarkableSearchItem,
                typename,
              ),
            }}
          />
        )
      }

      case 'UserSearchItem': {
        const content = contentItem as UserSearchItem

        return (
          <Card
            layout={layout}
            autoLayoutWidth={autoLayoutWidth}
            type={type}
            thumbnail={{
              src: content?.imageUrl || '',
              type: 'square',
              fullWidth: false,
            }}
            avatar={{
              src: content?.imageUrl || '',
            }}
            content={{
              title: content?.title,
              titleUrl: content?.url,
              titleTruncate: true,
              subTitle: content?.description || '',
              subTitleTruncate: type !== 'search',
            }}
            details={{}}
            footer={{
              contentType: contentTypeFormatter(content),
              tag,
            }}
          />
        )
      }
      case 'MeetingSessionSearchItem': {
        const content = contentItem as MeetingSessionSearchItem

        return (
          <Card
            layout={layout}
            autoLayoutWidth={autoLayoutWidth}
            type={type}
            thumbnail={{
              src: content?.imageUrl || '',
              type: 'square',
              fullWidth: false,
            }}
            avatar={{
              src: content?.imageUrl || '',
            }}
            content={{
              title: content?.title,
              titleUrl: content?.url,
              titleTruncate: true,
              subTitle: content?.description || '',
              subTitleTruncate: type !== 'search',
            }}
            details={{}}
            footer={{
              contentType: contentTypeFormatter(content),
              tag,
            }}
          />
        )
      }

      default:
        return <></>
    }
  }

  return cardChooser()
}

export default ContentCard
